import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, input, model, output, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, map } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { SelectModule } from 'primeng/select';
import { DividerModule } from 'primeng/divider';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { DatePickerModule } from 'primeng/datepicker';
import { UsuarioService } from '@/core/api/sicom-api/sistema/usuario/usuario.service';
import { EstruturaService } from '@/core/api/sicom-api/sistema/estrutura/estrutura.service';
import { CargoService } from '@/core/api/sicom-api/sistema/cargo/cargo.service';
import { CargoResponse } from '@/core/api/sicom-api/sistema/cargo/cargo.model';
import { NotificacoesService } from '@/core/services/notificacoes.service';
import {
    SasUsuarioSearchResponse,
    UsuarioCriarCompletoRequest,
    UsuarioEstruturaRequest,
    UsuarioEstruturaResponse,
    UsuarioResumoResponse,
    UsuarioResponse,
    UsuarioRequest,
    FuncionarioDadosRequest
} from '@/core/api/sicom-api/sistema/usuario/usuario.model';
import { EstruturaResponse } from '@/core/api/sicom-api/sistema/estrutura/estrutura.model';
import { PesquisaUsuario } from '../../pesquisa-usuario/pesquisa-usuario';
import { Tabs, TabList, Tab, TabPanels, TabPanel } from 'primeng/tabs';
import { TableModule } from 'primeng/table';
import { FormsModule } from '@angular/forms';
import { TagModule } from 'primeng/tag';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { PermissionService } from '@/core/permissions/permission.service';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { MascaraUtil } from '@/shared/utils/mascara-util';
import { FormularioValidatorUtil } from '@/shared/utils/formulario-validator-util';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
type ModoCreate = 'vincular-sas' | 'criar-zero';
type Passo = 'selecionar-modo' | 'buscar-sas' | 'formulario-vincular' | 'formulario-completo';

/** Formulário de vínculo (usuário existente do SAS → portal). */
type FormVincular = FormGroup<{
    nmSolicitante: FormControl<string>;
    email: FormControl<string>;
    userId: FormControl<string>;
    dsBiografia: FormControl<string>;
    inAtivo: FormControl<string>;
}>;
/** Formulário de criação completa (usuário do zero, com UF e CPF). */
type FormCompleto = FormGroup<{
    uf: FormControl<string>;
    nmSolicitante: FormControl<string>;
    cpf: FormControl<string>;
    email: FormControl<string>;
    userId: FormControl<string>;
    dsBiografia: FormControl<string>;
    inAtivo: FormControl<string>;
}>;

const UFS = ['BR', 'AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS', 'MT', 'PA', 'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO'].map((s) => ({ label: s, value: s }));

@Component({
    selector: 'app-usuario-detalhe',
    standalone: true,
    imports: [
        DialogoPadraoDirective,
        FormsModule,
        ReactiveFormsModule,
        ButtonModule,
        Dialog,
        InputTextModule,
        TextareaModule,
        SelectModule,
        DividerModule,
        ToggleSwitchModule,
        DatePickerModule,
        PesquisaUsuario,
        Tabs,
        TabList,
        Tab,
        TabPanels,
        TabPanel,
        TableModule,
        TagModule,
        CanDirective,
        ConfirmDialogModule,
        NgTemplateOutlet
    ],
    providers: [ConfirmationService],
    templateUrl: './usuario-detalhe.html',
    styleUrl: './usuario-detalhe.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
/** Diálogo de novo/edição de usuário do portal: vincular a solicitante SAS ou criar do zero, com setores e Keycloak. */
export class UsuarioDetalhe {
    private readonly fb = inject(FormBuilder);
    private readonly usuarioService = inject(UsuarioService);
    private readonly estruturaService = inject(EstruturaService);
    private readonly cargoService = inject(CargoService);
    private readonly notificacoes = inject(NotificacoesService);
    private readonly confirmationService = inject(ConfirmationService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly permissionService = inject(PermissionService);

    usuario = input<UsuarioResumoResponse | null>(null);
    showDialog = model.required<boolean>();
    salvo = output<void>();

    protected passo = signal<Passo>('selecionar-modo');
    protected sasSelecionado = signal<SasUsuarioSearchResponse | null>(null);
    protected lookupKeycloak = signal<'idle' | 'buscando' | 'ok' | 'nao-encontrado' | 'sem-cpf' | 'criando' | 'erro'>('idle');
    protected salvando = signal(false);
    protected formSubmitted = false;

    protected readonly ufs = UFS;
    protected readonly opcoesStatus = [
        { label: 'Ativo', value: 'S' },
        { label: 'Inativo', value: 'N' }
    ];
    protected readonly roleUsuariosCriar = PERMISSOES.configuracoes.usuarios.criar;
    protected readonly roleUsuariosEditar = PERMISSOES.configuracoes.usuarios.editar;
    protected readonly roleUsuariosEditarAgr = PERMISSOES.configuracoes.usuarios.editarAgr;

    protected fotoBase64 = signal<string | null>(null);
    protected emailsSolicitante = signal<string[]>([]);
    private emailOriginal = '';

    // --- Alteração de CPF (modo edição) ---
    protected cpfOriginal = signal<string>('');
    protected cpfEditavel = signal(false);
    protected cpfInput = signal<string>('');
    protected cpfTocado = signal(false);
    /** CPF atual formatado para exibição read-only. */
    protected cpfFormatadoAtual = computed(() => (this.cpfOriginal() ? MascaraUtil.cpfCnpj(this.cpfOriginal()) : 'Não informado'));
    protected cpfNovoValido = computed(() => FormularioValidatorUtil.cpfValido(this.cpfInput()));
    /** Só considera alteração quando o CPF digitado está completo e difere do original. */
    protected cpfAlterado = computed(() => {
        const digitos = this.cpfInput().replace(/\D/g, '');
        return digitos.length === 11 && digitos !== this.cpfOriginal();
    });

    // --- CPF do solicitante que não tem um (só no vínculo, modo criação) ---
    /** CPF digitado, com máscara, para resolver o Keycloak e ser gravado na SOLICITANTE. */
    protected cpfSolicitante = signal<string>('');

    /**
     * Latch: liga quando o 422 do `resolverUserId` diz que o solicitante não tem CPF, e só desliga
     * nos mesmos três pontos que zeram `cpfSolicitante` (`inicializar`, `onSasSelecionado`,
     * `voltarParaBusca`). Existe para o campo de CPF não sumir da tela assim que a consulta que ele
     * dispara sai do estado `'sem-cpf'` (p.ex. para `'buscando'` ou `'ok'`) — sem o latch, o campo
     * desaparecia no instante em que passava a ter mais utilidade: conferir o valor antes de salvar,
     * ou corrigi-lo depois de um erro.
     */
    protected solicitanteSemCpf = signal(false);

    /** O solicitante escolhido não tem CPF: sem informá-lo, não há como resolver o User ID. */
    protected precisaInformarCpf = computed(() => this.solicitanteSemCpf());

    /** CPF com 11 dígitos mas DV inválido — dígitos certos mas incompletos não contam como erro. */
    protected cpfSolicitanteInvalido = computed(() => {
        const digitos = this.cpfSolicitante().replace(/\D/g, '');
        return digitos.length === 11 && !FormularioValidatorUtil.cpfValido(this.cpfSolicitante());
    });

    protected formVincular!: FormVincular;
    protected formCompleto!: FormCompleto;

    /** A pessoa escolhida ainda não tem registro de funcionário: o vínculo vai criá-lo. */
    protected precisaCriarFuncionario = computed(() => this.sasSelecionado()?.ehFuncionario === false);

    /**
     * Inicializado já na declaração (não só em `inicializar()`): testes e outros consumidores podem
     * acessar `formFuncionario` antes do primeiro ciclo do `effect()` do diálogo, do mesmo jeito que
     * fariam com um signal comum.
     */
    protected formFuncionario: FormGroup<{
        nuMatricula: FormControl<string>;
        idCargo: FormControl<number | null>;
        dtNascimento: FormControl<Date | null>;
        dtAdmissao: FormControl<Date | null>;
        dtAfastamento: FormControl<Date | null>;
        inConselheiro: FormControl<string>;
        inAscfm: FormControl<string>;
    }> = this.novoFormFuncionario();

    // --- Setores ---
    protected setores = signal<UsuarioEstruturaResponse[]>([]);
    protected carregandoSetores = signal(false);
    protected adicionandoSetor = signal(false);
    protected removendoSetorId = signal<number | null>(null);
    protected editandoSetor = signal<UsuarioEstruturaResponse | null>(null);

    protected todasEstruturas = signal<EstruturaResponse[]>([]);
    protected todosCargos = signal<CargoResponse[]>([]);
    protected novoSetor = { idEstrutura: null as number | null, idCargo: null as number | null, inStatus: 'S' };

    // --- Agente de Registro (AGR) ---
    // Tri-estado: true/false = é funcionário; null = sem cadastro de funcionário, não pode receber
    // a marca. É atributo de DADO do usuário, não permissão.
    protected agenteAgr = signal<boolean | null>(null);
    protected salvandoAgr = signal(false);
    protected agrIndisponivel = computed(() => this.agenteAgr() === null);

    /**
     * Só quem tem a role dedicada altera o AGR — a permissão de editar usuário não serve.
     * Reativo: `can()` deriva do signal de roles, então o controle se habilita sozinho quando
     * as roles chegam no boot.
     */
    protected podeEditarAgr = computed(() => this.permissionService.can(this.roleUsuariosEditarAgr));

    /**
     * Por que o controle está bloqueado, ou `null` se está liberado. A ordem importa: a falta de
     * cadastro de funcionário prevalece sobre a falta de permissão, por ser a condição mais
     * concreta — dizer "sem permissão" a quem também não teria o que marcar seria enganoso.
     */
    protected motivoAgrBloqueado = computed<string | null>(() => {
        if (this.passo() === 'formulario-completo') {
            return 'Solicitante novo ainda não tem cadastro de funcionário.';
        }
        if (this.usuario() !== null && this.agenteAgr() === null) {
            return 'Usuário sem cadastro de funcionário.';
        }
        if (!this.podeEditarAgr()) {
            return 'Sem permissão para alterar.';
        }
        return null;
    });

    protected agrBloqueado = computed(() => this.motivoAgrBloqueado() !== null);

    constructor() {
        effect(() => {
            if (this.showDialog()) this.inicializar();
        });
    }

    get modoEdicao(): boolean {
        return this.usuario() !== null;
    }
    get titulo(): string {
        return this.modoEdicao ? 'Editar Usuário' : 'Novo Usuário';
    }

    private inicializar(): void {
        this.formSubmitted = false;
        this.salvando.set(false);
        this.sasSelecionado.set(null);
        this.lookupKeycloak.set('idle');
        this.fotoBase64.set(null);
        this.setores.set([]);
        this.novoSetor = { idEstrutura: null, idCargo: null, inStatus: 'S' };
        this.editandoSetor.set(null);
        this.cpfEditavel.set(false);
        this.cpfInput.set('');
        this.cpfTocado.set(false);
        this.cpfOriginal.set(this.usuario()?.cpf ?? '');
        this.cpfSolicitante.set('');
        this.solicitanteSemCpf.set(false);
        this.agenteAgr.set(null);
        this.salvandoAgr.set(false);
        this.formFuncionario = this.novoFormFuncionario();

        if (this.modoEdicao) {
            this.passo.set('formulario-vincular');
            this.carregarDetalhe(this.usuario()!.id);
            this.carregarSetores(this.usuario()!.id);
            this.carregarEstruturas();
            this.carregarCargos();
        } else {
            this.passo.set('selecionar-modo');
        }
    }

    private carregarSetores(idUsuario: number): void {
        this.carregandoSetores.set(true);
        this.usuarioService
            .listarSetores(idUsuario)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (lista) => {
                    this.setores.set(lista);
                    this.carregandoSetores.set(false);
                },
                error: () => this.carregandoSetores.set(false)
            });
    }

    private carregarEstruturas(): void {
        if (this.todasEstruturas().length > 0) return;
        this.estruturaService
            .listar()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({ next: (lista) => this.todasEstruturas.set(lista) });
    }

    private carregarCargos(): void {
        if (this.todosCargos().length > 0) return;
        this.cargoService
            .listar()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({ next: (lista) => this.todosCargos.set(lista) });
    }

    protected opcoesCargos() {
        return this.todosCargos().map((c) => ({ label: c.dsCargo, value: c.id }));
    }

    protected opcoesEstruturas() {
        const usados = new Set(this.setores().map((s) => s.idEstrutura));
        return this.todasEstruturas()
            .filter((e) => !usados.has(e.id) || this.editandoSetor()?.idEstrutura === e.id)
            .map((e) => ({ label: `#${e.id} — ${e.descricao}`, value: e.id }));
    }

    protected iniciarEdicaoSetor(setor: UsuarioEstruturaResponse): void {
        this.editandoSetor.set({ ...setor });
    }

    protected cancelarEdicaoSetor(): void {
        this.editandoSetor.set(null);
    }

    protected salvarEdicaoSetor(): void {
        const setor = this.editandoSetor();
        const idUsuario = this.usuario()?.id;
        if (!setor || !idUsuario) return;

        const request: UsuarioEstruturaRequest = {
            idEstrutura: setor.idEstrutura,
            idCargo: setor.idCargo,
            inStatus: setor.inStatus
        };

        this.usuarioService
            .atualizarSetor(idUsuario, setor.idEstrutura, request)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (atualizado) => {
                    this.setores.update((list) => list.map((s) => (s.idEstrutura === atualizado.idEstrutura ? atualizado : s)));
                    this.editandoSetor.set(null);
                    this.notificacoes.notificar({ severity: 'success', summary: 'Setor atualizado com sucesso.' });
                },
                error: () => this.notificacoes.notificar({ toast: false, severity: 'error', summary: 'Erro ao atualizar setor.' })
            });
    }

    protected adicionarSetor(): void {
        const idUsuario = this.usuario()?.id;
        if (!idUsuario || !this.novoSetor.idEstrutura) return;

        this.adicionandoSetor.set(true);
        const request: UsuarioEstruturaRequest = {
            idEstrutura: this.novoSetor.idEstrutura,
            idCargo: this.novoSetor.idCargo,
            inStatus: this.novoSetor.inStatus
        };

        this.usuarioService
            .adicionarSetor(idUsuario, request)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (novo) => {
                    this.setores.update((list) => [...list, novo]);
                    this.novoSetor = { idEstrutura: null, idCargo: null, inStatus: 'S' };
                    this.adicionandoSetor.set(false);
                    this.notificacoes.notificar({ severity: 'success', summary: 'Setor adicionado com sucesso.' });
                },
                error: () => {
                    this.adicionandoSetor.set(false);
                    this.notificacoes.notificar({ toast: false, severity: 'error', summary: 'Erro ao adicionar setor.' });
                }
            });
    }

    protected removerSetor(setor: UsuarioEstruturaResponse): void {
        const idUsuario = this.usuario()?.id;
        if (!idUsuario) return;

        this.removendoSetorId.set(setor.idEstrutura);
        this.usuarioService
            .removerSetor(idUsuario, setor.idEstrutura)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.setores.update((list) => list.filter((s) => s.idEstrutura !== setor.idEstrutura));
                    this.removendoSetorId.set(null);
                    this.notificacoes.notificar({ severity: 'success', summary: 'Setor removido com sucesso.' });
                },
                error: () => {
                    this.removendoSetorId.set(null);
                    this.notificacoes.notificar({ toast: false, severity: 'error', summary: 'Erro ao remover setor.' });
                }
            });
    }

    private carregarDetalhe(id: number): void {
        this.usuarioService
            .buscarPorId(id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (d) => {
                    this.fotoBase64.set(d.avatar ?? null);
                    this.cpfOriginal.set(d.cpf ?? '');
                    this.agenteAgr.set(d.agenteAgr ?? null);
                    this.inicializarFormVincular(d);
                },
                error: () => this.notificacoes.notificar({ toast: false, severity: 'error', summary: 'Erro ao carregar usuário' })
            });
    }

    protected onFotoSelecionada(event: Event): void {
        const file = (event.target as HTMLInputElement).files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => this.fotoBase64.set(reader.result as string);
        reader.readAsDataURL(file);
    }

    protected removerFoto(): void {
        this.fotoBase64.set(null);
    }

    private inicializarFormVincular(detalhe: UsuarioResponse | null): void {
        this.formVincular = this.fb.nonNullable.group({
            nmSolicitante: [detalhe?.nmSolicitante ?? '', [Validators.required, Validators.maxLength(70)]],
            email: [detalhe?.email ?? '', [Validators.email, Validators.maxLength(60)]],
            userId: [detalhe?.userId ?? '', [Validators.required, Validators.maxLength(300)]],
            dsBiografia: [detalhe?.dsBiografia ?? '', Validators.maxLength(200)],
            inAtivo: [detalhe?.inAtivo ?? 'S', Validators.required]
        });
        this.formVincular.get('userId')?.disable();
    }

    private inicializarFormCompleto(): void {
        this.formCompleto = this.fb.nonNullable.group({
            uf: ['', Validators.required],
            nmSolicitante: ['', [Validators.required, Validators.maxLength(70)]],
            cpf: ['', [Validators.required, Validators.maxLength(20)]],
            email: ['', [Validators.email, Validators.maxLength(50)]],
            userId: ['', [Validators.required, Validators.maxLength(300)]],
            dsBiografia: ['', Validators.maxLength(200)],
            inAtivo: ['S', Validators.required]
        });
        this.formCompleto.get('userId')?.disable();
        this.lookupKeycloak.set('idle');
        this.formCompleto
            .get('cpf')!
            .valueChanges.pipe(
                debounceTime(400),
                map((v: string) => (v ?? '').replace(/\D/g, '')),
                distinctUntilChanged(),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((digits) => {
                if (digits.length === 11) {
                    this.resolverPorCpf(digits);
                } else {
                    this.lookupKeycloak.set('idle');
                }
            });
    }

    /** Nenhum campo é obrigatório — a exigência do negócio é ter o registro, não preencher tudo. */
    private novoFormFuncionario() {
        return this.fb.nonNullable.group({
            nuMatricula: ['', Validators.maxLength(5)],
            idCargo: this.fb.control<number | null>(null),
            dtNascimento: this.fb.control<Date | null>(null),
            dtAdmissao: this.fb.control<Date | null>(null),
            dtAfastamento: this.fb.control<Date | null>(null),
            inConselheiro: ['N'],
            inAscfm: ['N']
        });
    }

    private resolverPorCpf(cpf: string): void {
        this.lookupKeycloak.set('buscando');
        this.usuarioService
            .buscarUserIdKeycloakPorCpf(cpf)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (r) => {
                    this.formCompleto.patchValue({ userId: r.userId });
                    if (r.nome) this.formCompleto.patchValue({ nmSolicitante: r.nome });
                    if (r.email) this.formCompleto.patchValue({ email: r.email });
                    this.formCompleto.get('cpf')?.disable({ emitEvent: false });
                    this.lookupKeycloak.set('ok');
                },
                error: (e) => this.lookupKeycloak.set(this.mapErroKeycloak(e))
            });
    }

    protected onEmailBlurCompleto(): void {
        if (this.lookupKeycloak() !== 'nao-encontrado') return;
        const ctrl = this.formCompleto.get('email');
        const email = (ctrl?.value ?? '').trim();
        if (!email || ctrl?.invalid) return;
        this.criarNoKeycloakCompleto(email);
    }

    protected criarNoKeycloakCompleto(email: string): void {
        const cpf = (this.formCompleto.get('cpf')?.value ?? '').replace(/\D/g, '');
        const nome = this.formCompleto.get('nmSolicitante')?.value ?? '';
        if (cpf.length !== 11) return;
        this.lookupKeycloak.set('criando');
        this.usuarioService
            .criarUsuarioKeycloakPorCpf(cpf, nome, email)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (r) => {
                    this.formCompleto.patchValue({ userId: r.userId });
                    this.formCompleto.get('cpf')?.disable({ emitEvent: false });
                    this.lookupKeycloak.set('ok');
                    this.notificacoes.notificar({ severity: 'success', summary: 'Usuário criado no Keycloak.' });
                },
                error: () => this.lookupKeycloak.set('erro')
            });
    }

    protected selecionarModo(modo: ModoCreate): void {
        if (modo === 'vincular-sas') {
            this.passo.set('buscar-sas');
        } else {
            this.inicializarFormCompleto();
            this.passo.set('formulario-completo');
        }
    }

    protected onSasSelecionado(sas: SasUsuarioSearchResponse): void {
        if (sas.jaVinculado) {
            this.notificacoes.notificar({ severity: 'warn', summary: 'Este solicitante já possui usuário no portal.' });
            return;
        }
        this.sasSelecionado.set(sas);
        this.inicializarFormVincular(null);
        this.formVincular.patchValue({ nmSolicitante: sas.nmSolicitante });
        this.formFuncionario = this.novoFormFuncionario();
        this.cpfSolicitante.set('');
        this.solicitanteSemCpf.set(false);
        this.emailsSolicitante.set([]);
        this.carregarEmailsSolicitante(sas.idSolicitante, sas.sgUf);
        this.carregarCargos();
        this.passo.set('formulario-vincular');
        this.resolverUserId(sas);
    }

    private carregarEmailsSolicitante(idSolicitante: number, uf: string): void {
        this.usuarioService
            .listarEmailsSolicitante(idSolicitante, uf)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (emails) => {
                    const atual = (this.formVincular.get('email')?.value ?? '').trim();
                    const lista = emails ?? [];
                    this.emailsSolicitante.set(atual && !lista.includes(atual) ? [atual, ...lista] : lista);
                }
            });
    }

    /** Garante que o e-mail (prefilled do Keycloak ou digitado) apareça como opção no combo. */
    private adicionarOpcaoEmail(email: string): void {
        const e = (email ?? '').trim();
        if (!e) return;
        this.emailsSolicitante.update((list) => (list.includes(e) ? list : [e, ...list]));
    }

    private resolverUserId(sas: SasUsuarioSearchResponse): void {
        if (!sas.idSolicitante || !sas.sgUf) {
            this.lookupKeycloak.set('erro');
            return;
        }
        this.lookupKeycloak.set('buscando');
        this.usuarioService
            .buscarUserIdKeycloak(sas.idSolicitante, sas.sgUf)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (r) => {
                    this.formVincular.patchValue({ userId: r.userId });
                    const emailKc = r.email ?? '';
                    if (emailKc) {
                        this.formVincular.patchValue({ email: emailKc });
                        this.adicionarOpcaoEmail(emailKc);
                    }
                    this.emailOriginal = emailKc;
                    this.lookupKeycloak.set('ok');
                },
                error: (e) => {
                    const estado = this.mapErroKeycloak(e);
                    if (estado === 'sem-cpf') {
                        this.solicitanteSemCpf.set(true);
                    }
                    this.lookupKeycloak.set(estado);
                }
            });
    }

    private mapErroKeycloak(e: { status?: number }): 'nao-encontrado' | 'sem-cpf' | 'erro' {
        if (e?.status === 404) return 'nao-encontrado';
        if (e?.status === 422) return 'sem-cpf';
        return 'erro';
    }

    protected onCpfSolicitanteInput(valor: string): void {
        this.cpfSolicitante.set(MascaraUtil.cpfCnpj(valor));
        const digitos = this.cpfSolicitante().replace(/\D/g, '');
        if (digitos.length !== 11 || !FormularioValidatorUtil.cpfValido(this.cpfSolicitante())) {
            return;
        }
        this.resolverVinculoPorCpf(digitos);
    }

    /**
     * Resolve o usuário no Keycloak pelo CPF digitado, quando o solicitante não tem um cadastrado.
     * Espelha o `resolverPorCpf` do modo "criar do zero" — mesma chamada, mesmo tratamento de erro —
     * mas escreve no formulário de vínculo.
     */
    private resolverVinculoPorCpf(cpf: string): void {
        this.lookupKeycloak.set('buscando');
        this.usuarioService
            .buscarUserIdKeycloakPorCpf(cpf)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (r) => {
                    this.formVincular.patchValue({ userId: r.userId });
                    if (r.email) {
                        this.formVincular.patchValue({ email: r.email });
                        this.adicionarOpcaoEmail(r.email);
                        this.emailOriginal = r.email;
                    }
                    this.lookupKeycloak.set('ok');
                },
                error: (e) => this.lookupKeycloak.set(this.mapErroKeycloak(e))
            });
    }

    protected onEmailBlurVincular(): void {
        if (this.lookupKeycloak() !== 'nao-encontrado') return;
        const ctrl = this.formVincular.get('email');
        const email = (ctrl?.value ?? '').trim();
        if (!email || ctrl?.invalid) return;
        this.criarNoKeycloak(email);
    }

    protected criarNoKeycloak(email: string): void {
        const sas = this.sasSelecionado();
        if (!sas) return;
        const cpf = this.cpfSolicitante().replace(/\D/g, '');
        // Segunda barreira (a primeira é o onCpfSolicitanteInput não chamar resolverVinculoPorCpf):
        // um CPF de 11 dígitos com DV inválido nunca pode seguir para a rota por CPF — criaria
        // conta no Keycloak com um CPF que o backend vai rejeitar ao salvar, deixando conta órfã e
        // e-mail de redefinição de senha enviado à toa.
        if (cpf.length === 11 && !FormularioValidatorUtil.cpfValido(this.cpfSolicitante())) {
            return;
        }
        this.lookupKeycloak.set('criando');
        const criar$ = cpf.length === 11 ? this.usuarioService.criarUsuarioKeycloakPorCpf(cpf, this.formVincular.get('nmSolicitante')?.value ?? '', email) : this.usuarioService.criarUsuarioKeycloak(sas.idSolicitante, sas.sgUf, email);
        criar$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: (r) => {
                this.formVincular.patchValue({ userId: r.userId });
                this.adicionarOpcaoEmail(email);
                this.emailOriginal = email;
                this.lookupKeycloak.set('ok');
                this.notificacoes.notificar({ severity: 'success', summary: 'Usuário criado no Keycloak.' });
            },
            error: (e) => this.lookupKeycloak.set(this.mapErroKeycloak(e))
        });
    }

    protected voltarParaModo(): void {
        this.passo.set('selecionar-modo');
        this.sasSelecionado.set(null);
        this.agenteAgr.set(null);
    }

    protected voltarParaBusca(): void {
        this.passo.set('buscar-sas');
        this.sasSelecionado.set(null);
        this.agenteAgr.set(null);
        this.formFuncionario = this.novoFormFuncionario();
        this.cpfSolicitante.set('');
        this.solicitanteSemCpf.set(false);
    }

    protected isInvalidV(campo: string): boolean {
        const c = this.formVincular?.get(campo);
        return (c?.invalid && (c.dirty || c.touched || this.formSubmitted)) ?? false;
    }

    protected isInvalidC(campo: string): boolean {
        const c = this.formCompleto?.get(campo);
        return (c?.invalid && (c.dirty || c.touched || this.formSubmitted)) ?? false;
    }

    protected habilitarEdicaoCpf(): void {
        this.cpfInput.set(MascaraUtil.cpfCnpj(this.cpfOriginal()));
        this.cpfTocado.set(false);
        this.cpfEditavel.set(true);
    }

    protected cancelarEdicaoCpf(): void {
        this.cpfEditavel.set(false);
        this.cpfInput.set('');
        this.cpfTocado.set(false);
    }

    protected onCpfInput(valor: string): void {
        this.cpfInput.set(MascaraUtil.cpfCnpj(valor));
    }

    protected alternarAgenteAgr(valor: boolean): void {
        const u = this.usuario();
        if (!u || this.agrBloqueado()) return;

        const anterior = this.agenteAgr();
        this.agenteAgr.set(valor);
        this.salvandoAgr.set(true);

        this.usuarioService
            .alterarAgenteAgr(u.id, valor)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.salvandoAgr.set(false);
                    this.notificacoes.notificar({ severity: 'success', summary: valor ? 'Usuário marcado como Agente de Registro.' : 'Marca de Agente de Registro removida.' });
                },
                error: (e: { status?: number }) => {
                    this.agenteAgr.set(anterior);
                    this.salvandoAgr.set(false);
                    if (e?.status === 403) {
                        this.notificacoes.notificar({ severity: 'error', summary: 'Sem permissão para alterar o Agente de Registro.' });
                        return;
                    }
                    const summary = e?.status === 422 ? 'Usuário não possui cadastro de funcionário.' : 'Erro ao alterar Agente de Registro.';
                    this.notificacoes.notificar({ toast: false, severity: 'error', summary });
                }
            });
    }

    /**
     * Handler do toggle. Em edição grava na hora, pelo endpoint dedicado; em cadastro só guarda a
     * intenção — não há id de usuário ainda, e a gravação acontece depois do save (ver a segunda
     * etapa em `persistirVincular`).
     */
    protected onAgrChange(valor: boolean): void {
        if (this.agrBloqueado()) return;
        if (this.modoEdicao) {
            this.alternarAgenteAgr(valor);
            return;
        }
        this.agenteAgr.set(valor);
    }

    protected salvarVincular(): void {
        this.formSubmitted = true;
        if (this.formVincular.invalid) return;
        if (!this.modoEdicao && this.lookupKeycloak() !== 'ok') return;

        // Barreira: correção de CPF confirma antes (altera também o login no Keycloak).
        if (this.modoEdicao && this.cpfEditavel() && this.cpfAlterado()) {
            if (!this.cpfNovoValido()) {
                this.cpfTocado.set(true);
                return;
            }
            this.confirmationService.confirm({
                key: 'cpf-confirm',
                header: 'Alterar CPF',
                message: 'O CPF e o login do usuário no Keycloak serão alterados. Deseja continuar?',
                icon: 'pi pi-exclamation-triangle',
                acceptLabel: 'Sim, alterar',
                rejectLabel: 'Cancelar',
                accept: () => this.aplicarCpfEPersistir()
            });
            return;
        }
        this.persistirVincular();
    }

    /** Corrige o CPF pelo endpoint dedicado (propaga ao Keycloak) e então persiste os demais dados. */
    private aplicarCpfEPersistir(): void {
        const u = this.usuario();
        if (!u) return;
        const cpf = this.cpfInput().replace(/\D/g, '');
        this.salvando.set(true);
        this.usuarioService
            .alterarCpf(u.id, cpf)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.cpfOriginal.set(cpf);
                    this.cpfEditavel.set(false);
                    this.notificacoes.notificar({ severity: 'success', summary: 'CPF alterado, inclusive o login no Keycloak.' });
                    this.persistirVincular();
                },
                error: (e: { status?: number }) => {
                    this.salvando.set(false);
                    const summary = e?.status === 409 ? 'CPF já em uso por outro usuário.' : e?.status === 400 ? 'CPF inválido.' : 'Erro ao alterar o CPF.';
                    this.notificacoes.notificar({ toast: false, severity: 'error', summary });
                }
            });
    }

    private persistirVincular(): void {
        const u = this.usuario();
        const sas = this.sasSelecionado();
        const fv = this.formVincular.getRawValue();

        const request: UsuarioRequest = {
            idSolicitante: (u?.idSolicitante ?? sas?.idSolicitante)!,
            ufSolicitante: (u?.ufSolicitante ?? sas?.sgUf)!,
            nmSolicitante: fv.nmSolicitante || undefined,
            email: fv.email || undefined,
            userId: fv.userId,
            dsBiografia: fv.dsBiografia || undefined,
            inAtivo: fv.inAtivo,
            avatar: this.fotoBase64() ?? undefined,
            funcionario: !u && this.precisaCriarFuncionario() ? this.dadosDoFuncionario() : undefined,
            cpf: this.cpfSolicitante().replace(/\D/g, '') || undefined
        };

        this.salvando.set(true);
        const op$ = u ? this.usuarioService.atualizar(u.id, request) : this.usuarioService.criar(request);

        op$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: (resp) => {
                this.notificacoes.notificar({ severity: 'success', summary: u ? 'Usuário atualizado' : 'Usuário vinculado com sucesso.' });
                if (resp?.avisoGrupo) {
                    this.notificacoes.notificar({ severity: 'warn', summary: resp.avisoGrupo });
                }
                if (resp?.avisoEmail) {
                    this.notificacoes.notificar({ severity: 'warn', summary: resp.avisoEmail });
                }
                if (resp?.avisoRole) {
                    this.notificacoes.notificar({ severity: 'warn', summary: resp.avisoRole });
                }
                // Criação com AGR ligado: segunda etapa, agora que existe id.
                if (!u && this.agenteAgr() === true && resp?.id != null) {
                    this.marcarAgrDoNovoUsuario(resp.id);
                    return;
                }
                this.concluirSalvamento();
            },
            error: (e: { status?: number }) => {
                // 400/409 passaram a ser respostas possíveis deste caminho desde que o CPF do
                // solicitante sem cadastro é validado (e checado contra outras contas) só no
                // backend — mesmo tratamento de aplicarCpfEPersistir, para o operador entender o
                // que houve em vez de um "erro ao salvar" genérico.
                const summary = e?.status === 409 ? 'CPF já em uso por outro usuário.' : e?.status === 400 ? 'CPF inválido.' : 'Erro ao salvar usuário';
                this.notificacoes.notificar({ toast: false, severity: 'error', summary });
                this.salvando.set(false);
            }
        });
    }

    private concluirSalvamento(): void {
        this.salvando.set(false);
        this.salvo.emit();
    }

    /** Só as datas viram ISO; o resto vai como está. Campo vazio vira `undefined`, não string vazia. */
    private dadosDoFuncionario(): FuncionarioDadosRequest {
        const f = this.formFuncionario.getRawValue();
        const dia = (d: Date | null) => (d ? d.toISOString().slice(0, 10) : undefined);
        return {
            nuMatricula: f.nuMatricula || undefined,
            idCargo: f.idCargo ?? undefined,
            dtNascimento: dia(f.dtNascimento),
            dtAdmissao: dia(f.dtAdmissao),
            dtAfastamento: dia(f.dtAfastamento),
            inConselheiro: f.inConselheiro,
            inAscfm: f.inAscfm
        };
    }

    /**
     * Segunda etapa da criação: marca o AGR do usuário recém-criado, já com o id devolvido pelo
     * backend.
     *
     * <p>Falhar aqui não invalida a criação — o usuário existe. Tratar como erro geral faria a
     * pessoa tentar de novo e colidir com o registro que acabou de ser criado; por isso vira aviso,
     * no mesmo padrão dos `avisoGrupo`/`avisoEmail`/`avisoRole` que o backend já devolve no create.
     * O caso esperado é o 422: o solicitante do SAS não tem cadastro de funcionário, e o front não
     * tem como saber disso antes de tentar.
     */
    private marcarAgrDoNovoUsuario(id: number): void {
        this.salvandoAgr.set(true);
        this.usuarioService
            .alterarAgenteAgr(id, true)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.salvandoAgr.set(false);
                    this.concluirSalvamento();
                },
                error: (e: { status?: number }) => {
                    this.salvandoAgr.set(false);
                    const motivo = e?.status === 403 ? 'sem permissão' : e?.status === 422 ? 'usuário não possui cadastro de funcionário' : 'falha ao gravar';
                    this.notificacoes.notificar({ severity: 'warn', summary: `Usuário criado, mas não foi possível marcá-lo como Agente de Registro: ${motivo}.` });
                    this.concluirSalvamento();
                }
            });
    }

    protected salvarCompleto(): void {
        this.formSubmitted = true;
        if (this.formCompleto.invalid) return;
        if (this.lookupKeycloak() !== 'ok') return;

        const fv = this.formCompleto.getRawValue();
        const request: UsuarioCriarCompletoRequest = {
            uf: fv.uf,
            nmSolicitante: fv.nmSolicitante,
            cpf: fv.cpf || undefined,
            email: fv.email || undefined,
            userId: fv.userId,
            dsBiografia: fv.dsBiografia || undefined,
            inAtivo: fv.inAtivo,
            avatar: this.fotoBase64() ?? undefined
        };

        this.salvando.set(true);
        this.usuarioService
            .criarCompleto(request)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (resp) => {
                    this.notificacoes.notificar({ severity: 'success', summary: 'Usuário criado com sucesso.' });
                    if (resp?.avisoGrupo) {
                        this.notificacoes.notificar({ severity: 'warn', summary: resp.avisoGrupo });
                    }
                    if (resp?.avisoEmail) {
                        this.notificacoes.notificar({ severity: 'warn', summary: resp.avisoEmail });
                    }
                    if (resp?.avisoRole) {
                        this.notificacoes.notificar({ severity: 'warn', summary: resp.avisoRole });
                    }
                    this.salvando.set(false);
                    this.salvo.emit();
                },
                error: () => {
                    this.notificacoes.notificar({ toast: false, severity: 'error', summary: 'Erro ao criar usuário' });
                    this.salvando.set(false);
                }
            });
    }

    protected fechar(): void {
        this.showDialog.set(false);
    }
}
